import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Calendar,
  CalendarClock,
  Flag,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Trash2,
  Plus,
  Pencil,
  Send,
  ThumbsUp,
  Lock,
  LifeBuoy,
  DollarSign,
  GitBranch,
  Milestone,
  Paperclip,
  FileSignature,
  MessageSquare,
  Archive,
  ArchiveRestore,
  Hourglass,
  ChevronDown,
} from "lucide-react";
import { buscarProjeto, deletarProjeto, atualizarProjeto, arquivarProjeto, desarquivarProjeto } from "../../services/projetos";
import { useAuth } from "../../hooks/useAuth";
import { NovaTarefaModal } from "../tarefas/NovaTarefaModal";
import { confirmarDuasVezes } from "../../utils/confirmarDuasVezes";
import { ProjetoFinanceiroSection } from "./ProjetoFinanceiroSection";
import { ProjetoAnexosSection } from "./ProjetoAnexosSection";
import { ProjetoComentariosSection } from "./ProjetoComentariosSection";
import { ProjetoAssinaturasSection } from "./ProjetoAssinaturasSection";
import { ProjetoKanbanEmbed } from "./ProjetoKanbanEmbed";
import { listarTiposProjeto } from "../../services/tiposProjeto";
import { listarGerentes } from "../../services/usuarios";
import { ProjetoAvatar } from "../../components/ProjetoAvatar";
import { listarProjetoTags } from "../../services/projetoTags";
import { BriefingSecao } from "../../components/BriefingSecao";
import { TagChip } from "../../components/TagChip";

const statusLabels: Record<string, string> = {
  planejamento: "Planejamento",
  ativo: "Ativo",
  em_validacao: "Em validação do cliente",
  pausado: "Pausado",
  entregue: "Entregue (em suporte)",
  concluido: "Concluído",
};

const statusColors: Record<string, string> = {
  planejamento: "bg-slate-100 text-slate-700",
  ativo: "bg-green-100 text-green-700",
  em_validacao: "bg-purple-100 text-purple-700",
  pausado: "bg-amber-100 text-amber-700",
  entregue: "bg-blue-100 text-blue-700",
  concluido: "bg-slate-200 text-slate-600",
};

// Prioridade do projeto saiu da UI (Rec, jul/2026) — substituída pelas tags.

type AbaProjeto = "financeiro" | "fases" | "marcos" | "anexos" | "assinaturas" | "comentarios";

export function ProjetoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [showNovaTarefa, setShowNovaTarefa] = useState(false);
  const [aba, setAba] = useState<AbaProjeto>("comentarios");

  const { data: projeto, isLoading } = useQuery({
    queryKey: ["projeto", id],
    queryFn: () => buscarProjeto(id!),
    enabled: !!id,
  });
  const { data: tiposProjeto = [] } = useQuery({
    queryKey: ["tipos-projeto", "ativos"],
    queryFn: () => listarTiposProjeto(),
  });
  // Tags do projeto (substituem a prioridade) — catálogo + slugs deste projeto.
  const { data: catalogoTagsProjeto = [] } = useQuery({
    queryKey: ["projeto-tags"],
    queryFn: () => listarProjetoTags(),
  });
  const tagsProjeto: string[] = (projeto as any)?.etiquetas
    ? String((projeto as any).etiquetas).split(/[,\s]+/).filter(Boolean)
    : [];

  const deletarM = useMutation({
    mutationFn: () => deletarProjeto(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projetos"] });
      navigate("/projetos");
    },
  });

  const atualizarM = useMutation({
    mutationFn: (dados: any) => atualizarProjeto(id!, dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projeto", id] });
      qc.invalidateQueries({ queryKey: ["projetos"] });
      // A dashboard conta atrasados/aguardando cliente — precisa refrescar na hora.
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: any) => {
      alert(e?.response?.data?.error?.message || "Não foi possível salvar a alteração. Tente de novo.");
    },
  });

  const arquivarM = useMutation({
    mutationFn: () => arquivarProjeto(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projeto", id] });
      qc.invalidateQueries({ queryKey: ["projetos"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const desarquivarM = useMutation({
    mutationFn: () => desarquivarProjeto(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projeto", id] });
      qc.invalidateQueries({ queryKey: ["projetos"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  function hojeIso() { return new Date().toISOString().split("T")[0]; }

  function handleExcluir() {
    if (!projeto) return;
    const ok = confirmarDuasVezes({
      primeiraMensagem: `Excluir definitivamente o projeto "${projeto.nome}"?\n\nIsso apagará todas as tarefas, anexos, comentários, contratos e mensagens vinculados. Essa ação não pode ser desfeita.`,
      segundaMensagem: `CONFIRMAR exclusão de "${projeto.nome}"?\n\nEsta é a confirmação FINAL — todos os dados serão perdidos para sempre.`,
    });
    if (ok) deletarM.mutate();
  }

  function handleArquivar() {
    if (!projeto) return;
    const ok = confirmarDuasVezes({
      primeiraMensagem: `Arquivar o projeto "${projeto.nome}"?\n\nEle some da listagem padrão. Mensalidades, contratos e assinaturas continuam ativos.`,
      segundaMensagem: `Confirmar arquivamento de "${projeto.nome}"?\n\nVocê pode desarquivar depois se precisar.`,
    });
    if (ok) arquivarM.mutate();
  }

  if (isLoading) return <div className="text-slate-400">Carregando...</div>;
  if (!projeto) return <div className="text-slate-400">Projeto não encontrado</div>;

  const ehFinalizado = projeto.status === "entregue" || projeto.status === "concluido";
  // "Aguardando cliente" tira o projeto de "atrasado" (o atraso não é da equipe) —
  // mesma regra da dashboard, pra a tela do projeto não se contradizer com o resumo.
  const isAtrasado =
    !ehFinalizado &&
    !(projeto as any).aguardandoCliente &&
    !!projeto.prazoEntrega &&
    new Date(projeto.prazoEntrega) < new Date();

  // Dias restantes até o prazo (ou desde, se atrasado).
  // Projetos finalizados (entregue/concluido) NÃO mostram "atrasado" — mostram "Entregue".
  const dp = (() => {
    if (!projeto.prazoEntrega) return { texto: "sem prazo", cor: "bg-slate-100 text-slate-500" };
    // Prazo inválido/epoch (1969/1970) = sem prazo (evita "atrasado 20630d").
    if (isNaN(new Date(projeto.prazoEntrega).getTime()) || new Date(projeto.prazoEntrega).getFullYear() < 2000) {
      return { texto: "sem prazo", cor: "bg-slate-100 text-slate-500" };
    }
    if (ehFinalizado) {
      return projeto.status === "concluido"
        ? { texto: "Concluído", cor: "bg-slate-200 text-slate-600" }
        : { texto: "Entregue", cor: "bg-blue-100 text-blue-700" };
    }
    // Aguardando cliente: pill roxo neutro — coerente com o header (não é atraso da equipe).
    if ((projeto as any).aguardandoCliente) {
      return { texto: "aguardando cliente", cor: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" };
    }
    const prazo = new Date(projeto.prazoEntrega); prazo.setHours(0, 0, 0, 0);
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    const dias = Math.round((prazo.getTime() - hoje.getTime()) / 86400000);
    if (dias < 0) return { texto: `atrasado ${Math.abs(dias)}d`, cor: "bg-red-100 text-red-700" };
    if (dias === 0) return { texto: "hoje", cor: "bg-red-100 text-red-700" };
    if (dias === 1) return { texto: "amanhã", cor: "bg-orange-100 text-orange-700" };
    if (dias <= 7) return { texto: `em ${dias} dias`, cor: "bg-orange-100 text-orange-700" };
    if (dias <= 30) return { texto: `em ${dias} dias`, cor: "bg-amber-100 text-amber-700" };
    return { texto: `em ${dias} dias`, cor: "bg-slate-100 text-slate-600" };
  })();

  const tarefasStatus = {
    a_fazer: projeto.tarefas?.filter((t) => t.status === "a_fazer").length || 0,
    em_andamento: projeto.tarefas?.filter((t) => t.status === "em_andamento").length || 0,
    em_revisao: projeto.tarefas?.filter((t) => t.status === "em_revisao").length || 0,
    concluido: projeto.tarefas?.filter((t) => t.status === "concluido").length || 0,
  };

  return (
    <div className={`space-y-6 ${(projeto as any).arquivado ? "opacity-95" : ""}`}>
      {/* Banner de projeto arquivado */}
      {(projeto as any).arquivado && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-xl p-4 flex items-center gap-3 flex-wrap">
          <Archive size={18} className="text-amber-700 dark:text-amber-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Projeto arquivado</p>
            <p className="text-xs text-amber-700/80 dark:text-amber-300/80">
              {(projeto as any).arquivadoEm && (
                <>arquivado em {new Date((projeto as any).arquivadoEm).toLocaleDateString("pt-BR")} · </>
              )}
              Mensalidades, contratos e assinaturas vinculados continuam ativos normalmente.
            </p>
          </div>
          {can(["gestor", "admin"]) && (
            <button
              onClick={() => desarquivarM.mutate()}
              disabled={desarquivarM.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-sm font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
            >
              <ArchiveRestore size={14} /> {desarquivarM.isPending ? "Desarquivando…" : "Desarquivar"}
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/projetos" className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ArrowLeft size={20} className="text-slate-500" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <ProjetoAvatar
              nome={projeto.nome}
              fotoUrl={(projeto as any).fotoUrl}
              cor={tiposProjeto.find((tp) => tp.slug === projeto.tipo)?.cor || "#64748b"}
              tamanho="md"
              titulo={tiposProjeto.find((tp) => tp.slug === projeto.tipo)?.label || projeto.tipo}
            />
            <h1 className="text-2xl font-bold text-slate-800">{projeto.nome}</h1>
            {/* STATUS EDITÁVEL NO LUGAR (pedido da Thainá, ago/2026: "poderia
                estar junto com o Aguardando cliente, assim não preciso editar
                tooodos os projetos, só abro e atualizo o status"). Trocar de
                status era passar pela tela de edição inteira. Aqui o próprio
                selo vira um select — mesma mutation que o botão ao lado usa.
                Quem não pode editar continua vendo só o selo. */}
            {can(["gestor", "admin"]) ? (
              <span className="relative inline-flex">
                <select
                  value={projeto.status}
                  onChange={(e) => atualizarM.mutate({ status: e.target.value })}
                  disabled={atualizarM.isPending}
                  title="Trocar o status do projeto"
                  className={`appearance-none cursor-pointer pl-2.5 pr-6 py-1 rounded text-xs font-medium outline-none border border-transparent hover:border-slate-300 focus:border-blue-500 disabled:opacity-50 ${statusColors[projeto.status]}`}
                >
                  {Object.entries(statusLabels).map(([valor, rotulo]) => (
                    <option key={valor} value={valor}>{rotulo}</option>
                  ))}
                </select>
                <ChevronDown size={12} className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 opacity-60" />
              </span>
            ) : (
              <span className={`px-2.5 py-1 rounded text-xs font-medium ${statusColors[projeto.status]}`}>
                {statusLabels[projeto.status]}
              </span>
            )}
            {(projeto as any).interno && (
              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-xs font-medium uppercase tracking-wide whitespace-nowrap">
                Interno
              </span>
            )}
            {/* MELHORIA 26 — rótulo de periodicidade (mensal/trimestral/anual). */}
            {(projeto as any).periodicidade && (
              <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 text-xs font-medium capitalize whitespace-nowrap">
                {(projeto as any).periodicidade}
              </span>
            )}
            {(projeto as any).aguardandoCliente && !(projeto as any).interno && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 text-xs font-medium whitespace-nowrap">
                <Hourglass size={12} /> Aguardando cliente
              </span>
            )}
            {isAtrasado && !(projeto as any).interno && (
              <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                <AlertTriangle size={14} /> Atrasado
              </span>
            )}
          </div>
          {!(projeto as any).interno && <p className="text-slate-500">{projeto.cliente}</p>}
        </div>
        {can(["gestor", "admin"]) && !(projeto as any).interno && (
          <button
            onClick={() => atualizarM.mutate({ aguardandoCliente: !(projeto as any).aguardandoCliente })}
            disabled={atualizarM.isPending}
            title={
              (projeto as any).aguardandoCliente
                ? "Cliente respondeu? Clique para tirar de 'Aguardando cliente' (volta a contar como atraso da equipe)."
                : "Projeto parado esperando o cliente (material, aprovação, feedback…)? Marque aqui — ele sai de 'Projetos atrasados'."
            }
            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg font-medium transition-colors border disabled:opacity-50 ${
              (projeto as any).aguardandoCliente
                ? "bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
            }`}
          >
            {(projeto as any).aguardandoCliente ? <CheckCircle2 size={16} /> : <Hourglass size={16} />}
            Aguardando cliente
          </button>
        )}
        <button
          onClick={() => setShowNovaTarefa(true)}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
        >
          <Plus size={18} /> Nova tarefa
        </button>
        {can(["gestor", "admin"]) && (
          <Link
            to={`/projetos/${id}/editar`}
            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-3 py-2.5 rounded-lg font-medium transition-colors"
            title="Editar projeto"
          >
            <Pencil size={18} /> Editar
          </Link>
        )}
      </div>

      {/* Kanban embutido — drag-and-drop direto na página do projeto */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <ProjetoKanbanEmbed
          projetoId={id!}
          podeArrastar={can(["funcionario", "suporte", "gestor", "admin"])}
          kanbanFullHref={`/projetos/${id}/kanban`}
        />
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <InfoCard
          icon={CalendarClock}
          label="Data de início"
          value={
            <DataEditavel
              valorIso={projeto.dataInicio}
              podeEditar={can(["gestor", "admin"])}
              onSalvar={(iso) => atualizarM.mutate({ dataInicio: iso })}
              extra={projeto.dataInicio && (
                <span className="inline-flex w-fit px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                  {(() => {
                    const ini = new Date(projeto.dataInicio); ini.setHours(0, 0, 0, 0);
                    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
                    const dias = Math.round((hoje.getTime() - ini.getTime()) / 86400000);
                    if (dias < 0) return `começa em ${-dias}d`;
                    if (dias === 0) return "começou hoje";
                    if (dias === 1) return "há 1 dia";
                    return `há ${dias} dias`;
                  })()}
                </span>
              )}
            />
          }
        />
        <InfoCard
          icon={Calendar}
          label="Prazo de entrega"
          value={
            <>
              {(projeto as any).prazoTexto ? (
                // Avulso: prazo descritivo livre (ex.: "15 dias após briefing").
                <span className="text-sm text-slate-700">{(projeto as any).prazoTexto}</span>
              ) : (projeto as any).contratoIndeterminado ? (
                <span className="text-sm text-slate-500">Contrato por tempo indeterminado</span>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <span>{projeto.prazoEntrega && new Date(projeto.prazoEntrega).getFullYear() >= 2000 ? new Date(projeto.prazoEntrega).toLocaleDateString("pt-BR") : "—"}</span>
                  <span className={`inline-flex w-fit px-2 py-0.5 rounded text-xs font-medium ${dp.cor}`}>
                    {dp.texto}
                  </span>
                </div>
              )}
              {/* MELHORIA 16 — data combinada com o cliente, em roxo, só gestão vê. */}
              {can(["gestor", "admin"]) && (projeto as any).dataEntregaCliente && (
                <div className="mt-1.5 text-xs font-semibold text-purple-600 dark:text-purple-400">
                  🔒 Entrega ao cliente: {new Date((projeto as any).dataEntregaCliente).toLocaleDateString("pt-BR")}
                </div>
              )}
            </>
          }
          danger={isAtrasado}
        />
        <InfoCard icon={User} label="Atendimento" value={
          <ResponsavelEditavel
            atualId={projeto.responsavel.id}
            atualNome={projeto.responsavel.nome}
            podeEditar={can(["gestor", "admin"])}
            onSalvar={(rid) => atualizarM.mutate({ responsavelId: rid })}
          />
        } />
        {/* Prioridade saiu (Rec: todos os projetos têm a mesma importância) —
            no lugar, as TAGS personalizadas do projeto. */}
        <InfoCard icon={Flag} label="Tags" value={
          tagsProjeto.length > 0 ? (
            <span className="flex flex-wrap gap-1">
              {tagsProjeto.map((slug) => (
                <TagChip key={slug} tag={catalogoTagsProjeto.find((t) => t.slug === slug)} slug={slug} tamanho="xs" />
              ))}
            </span>
          ) : (
            <span className="text-xs text-slate-400">sem tags</span>
          )
        } />
        <InfoCard icon={CheckCircle2} label="Tarefas concluídas" value={`${tarefasStatus.concluido} / ${projeto.tarefas?.length || 0}`} />
      </div>

      {can(["gestor", "admin"]) && !(projeto as any).interno && ((projeto as any).valor != null || (projeto as any).valorRecorrente != null || (projeto as any).custoExtra != null) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold shrink-0">$</div>
          <div className="flex-1 flex gap-6 flex-wrap min-w-0">
            {(projeto as any).valor != null && (
              <ValorAmber
                label="Valor do projeto"
                valor={`R$ ${Number((projeto as any).valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                dica="cobrança única (avulso/setup)"
              />
            )}
            {(projeto as any).valorRecorrente != null && (
              <ValorAmber
                label="Recorrente"
                valor={`R$ ${Number((projeto as any).valorRecorrente).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                sufixo="/mês"
                dica="mensalidade após entrega"
              />
            )}
            {(projeto as any).custoExtra != null && (
              <ValorAmber
                label={`Custo extra (reembolso)${(projeto as any).custoExtraDescricao ? " — " + (projeto as any).custoExtraDescricao : ""}`}
                valor={`R$ ${Number((projeto as any).custoExtra).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
              />
            )}
          </div>
          <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-200 px-2 py-1 rounded shrink-0">só admin/gestor</span>
        </div>
      )}

      {/* BRIEFING editável do projeto — aparece se houver formulário pro tipo. */}
      <BriefingSecao
        escopo="projeto"
        alvoId={projeto.id}
        tipoProjetoSlug={projeto.tipo}
        podeEditar={can(["gestor", "admin", "funcionario"])}
      />

      {/* Itens do contrato (Cadastro #1) — quantidade, especificações e horas vendidas. */}
      {Array.isArray((projeto as any).itensMensais) && (projeto as any).itensMensais.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-medium text-slate-700 mb-3">Itens do contrato</h3>
          <div className="divide-y divide-slate-100">
            {(projeto as any).itensMensais.map((it: any) => (
              <div key={it.id} className="flex items-start gap-3 py-2">
                <span className="text-xs font-mono bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 shrink-0">{it.quantidade}×</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">{it.tipoTarefa?.label || "Item"}</p>
                  {(it.formato || it.especificacoes) && (
                    <p className="text-xs text-slate-500">
                      {[
                        it.formato ? String(it.formato).split(",").map((s: string) => s.trim()).filter(Boolean).map((s: string) => s.charAt(0).toUpperCase() + s.slice(1)).join(", ") : "",
                        it.especificacoes,
                      ].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                {it.horasVendidas != null && (
                  <span className="text-xs text-slate-500 shrink-0">{it.horasVendidas}h vendidas</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progresso */}
      {projeto.tarefas && projeto.tarefas.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-medium text-slate-700 mb-3">Progresso</h3>
          <div className="flex gap-1.5 mb-2">
            {["a_fazer", "em_andamento", "em_revisao", "concluido"].map((s) => {
              const total = projeto.tarefas?.length || 1;
              const pct = ((tarefasStatus[s as keyof typeof tarefasStatus] || 0) / total) * 100;
              const colors: Record<string, string> = {
                a_fazer: "bg-slate-300",
                em_andamento: "bg-blue-500",
                em_revisao: "bg-amber-500",
                concluido: "bg-green-500",
              };
              return <div key={s} className={`h-2 rounded-full ${colors[s]}`} style={{ width: `${pct}%` }} />;
            })}
          </div>
          <div className="flex gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-slate-300" /> A Fazer {tarefasStatus.a_fazer}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-blue-500" /> Em Andamento {tarefasStatus.em_andamento}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-500" /> Em Revisão {tarefasStatus.em_revisao}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500" /> Concluído {tarefasStatus.concluido}
            </span>
          </div>
        </div>
      )}

      {/* Descrição */}
      {projeto.descricao && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-medium text-slate-700 mb-4">Descrição</h3>
          <p className="text-slate-600 whitespace-pre-wrap">{projeto.descricao}</p>
        </div>
      )}

      {/* === Tabs com seções extras === */}
      {(() => {
        const ehInterno = !!(projeto as any).interno;
        const tabs: Array<{ id: AbaProjeto; label: string; icon: any; visivel: boolean }> = ([
          { id: "comentarios" as AbaProjeto, label: "Comentários", icon: MessageSquare, visivel: true },
          { id: "financeiro" as AbaProjeto, label: "Financeiro", icon: DollarSign, visivel: !ehInterno && can(["gestor", "admin"]) },
          { id: "fases" as AbaProjeto, label: "Fases", icon: GitBranch, visivel: !ehInterno },
          { id: "marcos" as AbaProjeto, label: "Marcos", icon: Milestone, visivel: true },
          { id: "anexos" as AbaProjeto, label: "Arquivos", icon: Paperclip, visivel: true },
          { id: "assinaturas" as AbaProjeto, label: "Assinaturas", icon: FileSignature, visivel: !ehInterno },
        ]).filter((t) => t.visivel);

        // Se a aba ativa foi escondida (ex: virou interno), cai pra primeira disponível.
        const abaAtiva = tabs.find((t) => t.id === aba) ? aba : tabs[0]?.id;

        return (
          <div className="space-y-3">
            {/* Barra de tabs */}
            <div className="flex bg-white rounded-xl border border-slate-200 overflow-x-auto p-1 gap-0.5">
              {tabs.map((t) => {
                const Icon = t.icon;
                const ativa = t.id === abaAtiva;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setAba(t.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap rounded-lg transition-colors ${
                      ativa
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    <Icon size={15} /> {t.label}
                  </button>
                );
              })}
            </div>

            {/* Conteúdo da aba */}
            <div>
              {abaAtiva === "financeiro" && (
                <ProjetoFinanceiroSection projeto={projeto as any} />
              )}

              {abaAtiva === "fases" && (
                <FasesTimeline
                  projeto={projeto as any}
                  podeOperar={can(["gestor", "admin"])}
                  onSalvarDatas={(dados) => atualizarM.mutate(dados)}
                  onMarcarPrimeiraEntrega={() => {
                    const ok = window.confirm(`Confirmar que a primeira entrega foi feita ao cliente hoje?\n\nO status do projeto vira "Em validação do cliente".`);
                    if (ok) atualizarM.mutate({ dataPrimeiraEntrega: hojeIso(), status: "em_validacao" });
                  }}
                  onMarcarFeedbackOk={() => {
                    const ok = window.confirm(`O cliente validou e está tudo certo?\n\nO projeto vira "Entregue" e entra no período de suporte.`);
                    if (ok) {
                      const upd: any = { feedbackRecebidoEm: hojeIso(), status: "entregue" };
                      if (!(projeto as any).inicioSuporte) upd.inicioSuporte = hojeIso();
                      atualizarM.mutate(upd);
                    }
                  }}
                  onEncerrarSuporte={() => {
                    const ok = window.confirm(`Encerrar o período de suporte e finalizar o projeto?\n\nO projeto vira "Concluído".`);
                    if (ok) atualizarM.mutate({ fimSuporte: hojeIso(), status: "concluido" });
                  }}
                  loading={atualizarM.isPending}
                />
              )}

              {abaAtiva === "marcos" && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="text-sm font-medium text-slate-700 mb-4">Marcos de entrega</h3>
                  {projeto.marcos && projeto.marcos.length > 0 ? (
                    <div className="space-y-2">
                      {projeto.marcos.map((marco) => {
                        const done = marco.status === "concluido";
                        const late = !done && new Date(marco.dataPrevista) < new Date();
                        return (
                          <div key={marco.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${done ? "bg-green-100 text-green-600" : late ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"}`}>
                              {done ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{marco.nome}</p>
                              <p className="text-xs text-slate-500">{new Date(marco.dataPrevista).toLocaleDateString("pt-BR")}</p>
                            </div>
                            {marco.descricao && <p className="text-xs text-slate-400">{marco.descricao}</p>}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-6">Nenhum marco cadastrado.</p>
                  )}
                </div>
              )}

              {abaAtiva === "anexos" && (
                <ProjetoAnexosSection projetoId={projeto.id} />
              )}

              {abaAtiva === "assinaturas" && (
                <ProjetoAssinaturasSection projetoId={projeto.id} />
              )}

              {abaAtiva === "comentarios" && (
                <ProjetoComentariosSection projetoId={projeto.id} />
              )}
            </div>
          </div>
        );
      })()}

      {/* Rodapé de ações de risco — discreto, longe do Editar */}
      {(can(["gestor", "admin"]) && !(projeto as any).arquivado) || can(["admin"]) ? (
        <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-700">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-3 font-medium">
            Ações irreversíveis
          </p>
          <div className="flex flex-wrap gap-2">
            {can(["gestor", "admin"]) && !(projeto as any).arquivado && (
              <button
                onClick={handleArquivar}
                disabled={arquivarM.isPending}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors disabled:opacity-50"
                title="Arquivar projeto (sai da listagem padrão; mensalidades e assinaturas continuam ativos)"
              >
                <Archive size={13} /> {arquivarM.isPending ? "Arquivando…" : "Arquivar projeto"}
              </button>
            )}
            {can(["admin"]) && (
              <button
                onClick={handleExcluir}
                disabled={deletarM.isPending}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800 transition-colors disabled:opacity-50"
                title="Excluir projeto definitivamente (irreversível)"
              >
                <Trash2 size={13} /> {deletarM.isPending ? "Excluindo…" : "Excluir projeto"}
              </button>
            )}
          </div>
          <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2">
            Cada ação pede confirmação dupla antes de executar.
          </p>
        </div>
      ) : null}

      {showNovaTarefa && (
        <NovaTarefaModal
          defaultProjetoId={projeto.id}
          onClose={() => {
            setShowNovaTarefa(false);
            qc.invalidateQueries({ queryKey: ["projeto", id] });
          }}
        />
      )}
    </div>
  );
}

import { LucideIcon } from "lucide-react";

function EditarFasesDatasModal({ projeto, loading, onClose, onSalvar }: {
  projeto: any;
  loading: boolean;
  onClose: () => void;
  onSalvar: (dados: any) => void;
}) {
  const toIso = (d: string | null | undefined) => d ? d.split("T")[0] : "";
  const [dataPrimeiraEntrega, setDataPrimeiraEntrega] = useState(toIso(projeto.dataPrimeiraEntrega));
  const [prazoFeedback, setPrazoFeedback] = useState(toIso(projeto.prazoFeedback));
  const [feedbackRecebidoEm, setFeedbackRecebidoEm] = useState(toIso(projeto.feedbackRecebidoEm));
  const [inicioSuporte, setInicioSuporte] = useState(toIso(projeto.inicioSuporte));
  const [fimSuporte, setFimSuporte] = useState(toIso(projeto.fimSuporte));

  function salvar() {
    onSalvar({
      dataPrimeiraEntrega: dataPrimeiraEntrega || null,
      prazoFeedback: prazoFeedback || null,
      feedbackRecebidoEm: feedbackRecebidoEm || null,
      inicioSuporte: inicioSuporte || null,
      fimSuporte: fimSuporte || null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Editar datas das fases</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">Deixe em branco para limpar. Mudanças não alteram o status do projeto automaticamente — use os botões abaixo do timeline pra avançar fase.</p>

          <Campo label="Primeira entrega ao cliente" value={dataPrimeiraEntrega} onChange={setDataPrimeiraEntrega} />
          <Campo label="Prazo final do feedback do cliente" value={prazoFeedback} onChange={setPrazoFeedback} />
          <Campo label="Feedback recebido em" value={feedbackRecebidoEm} onChange={setFeedbackRecebidoEm} />

          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Suporte</p>
            <Campo label="Início do suporte" value={inicioSuporte} onChange={setInicioSuporte} />
            <Campo label="Fim do suporte" value={fimSuporte} onChange={setFimSuporte} />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
          <button onClick={salvar} disabled={loading} className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            {loading ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-xs text-slate-600 block mb-1">{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
      />
    </div>
  );
}

function ValorAmber({ label, valor, sufixo, dica }: { label: string; valor: string; sufixo?: string; dica?: string }) {
  // Reduz fonte conforme tamanho do valor pra não quebrar layout
  const tam = valor.length <= 10
    ? "text-2xl"
    : valor.length <= 13
      ? "text-xl"
      : valor.length <= 16
        ? "text-lg"
        : "text-base";
  return (
    <div className="min-w-0">
      <p className="text-xs text-amber-700 font-medium">{label}</p>
      <p
        className={`font-bold text-amber-900 tabular-nums whitespace-nowrap ${tam}`}
        title={`${valor}${sufixo || ""}`}
      >
        {valor}
        {sufixo && <span className="text-sm font-normal text-amber-700">{sufixo}</span>}
      </p>
      {dica && <p className="text-[10px] text-amber-700/80 font-normal mt-0.5 whitespace-normal">{dica}</p>}
    </div>
  );
}

function FasesTimeline({
  projeto,
  podeOperar,
  onSalvarDatas,
  onMarcarPrimeiraEntrega,
  onMarcarFeedbackOk,
  onEncerrarSuporte,
  loading,
}: {
  projeto: any;
  podeOperar: boolean;
  onSalvarDatas: (dados: any) => void;
  onMarcarPrimeiraEntrega: () => void;
  onMarcarFeedbackOk: () => void;
  onEncerrarSuporte: () => void;
  loading: boolean;
}) {
  const [editando, setEditando] = useState(false);
  const hoje = new Date();
  const fmt = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString("pt-BR") : null;

  const temFases =
    !!projeto.dataPrimeiraEntrega ||
    !!projeto.prazoFeedback ||
    !!projeto.feedbackRecebidoEm ||
    !!projeto.inicioSuporte ||
    !!projeto.fimSuporte;

  // Avisos de prazo
  const prazoFeedback = projeto.prazoFeedback ? new Date(projeto.prazoFeedback) : null;
  const feedbackAtrasado =
    prazoFeedback &&
    !projeto.feedbackRecebidoEm &&
    prazoFeedback < hoje;

  const fimSuporte = projeto.fimSuporte ? new Date(projeto.fimSuporte) : null;
  const suporteVencido = fimSuporte && fimSuporte < hoje && projeto.status === "entregue";
  const diasParaSuporteFim = fimSuporte
    ? Math.ceil((fimSuporte.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  if (!temFases && !podeOperar) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <LifeBuoy size={16} className="text-blue-600" /> Fases e suporte
        </h3>
        {podeOperar && (
          <button
            onClick={() => setEditando(true)}
            className="text-xs flex items-center gap-1 text-slate-500 hover:text-blue-600"
            title="Editar datas das fases"
          >
            <Pencil size={12} /> Editar datas
          </button>
        )}
      </div>

      {editando && (
        <EditarFasesDatasModal
          projeto={projeto}
          loading={loading}
          onClose={() => setEditando(false)}
          onSalvar={(dados) => {
            onSalvarDatas(dados);
            setEditando(false);
          }}
        />
      )}

      {/* Timeline horizontal */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <FaseCard
          icon={Send}
          titulo="1. Primeira entrega"
          dataLabel={projeto.dataPrimeiraEntrega ? `Entregue em ${fmt(projeto.dataPrimeiraEntrega)}` : "Pendente"}
          cor={projeto.dataPrimeiraEntrega ? "emerald" : "slate"}
          subtexto={projeto.prazoFeedback ? `Cliente tem até ${fmt(projeto.prazoFeedback)} pra dar feedback` : undefined}
        />
        <FaseCard
          icon={ThumbsUp}
          titulo="2. Validação do cliente"
          dataLabel={projeto.feedbackRecebidoEm ? `Validado em ${fmt(projeto.feedbackRecebidoEm)}` : feedbackAtrasado ? "Atrasado" : "Aguardando"}
          cor={projeto.feedbackRecebidoEm ? "emerald" : feedbackAtrasado ? "red" : "slate"}
          subtexto={projeto.prazoEntrega ? `Entrega final prevista: ${fmt(projeto.prazoEntrega)}` : undefined}
        />
        <FaseCard
          icon={LifeBuoy}
          titulo="3. Suporte"
          dataLabel={
            projeto.inicioSuporte
              ? projeto.fimSuporte
                ? `${fmt(projeto.inicioSuporte)} → ${fmt(projeto.fimSuporte)}`
                : `Desde ${fmt(projeto.inicioSuporte)}`
              : "Após entrega final"
          }
          cor={projeto.status === "concluido" ? "slate" : suporteVencido ? "red" : projeto.inicioSuporte ? "blue" : "slate"}
          subtexto={
            diasParaSuporteFim != null && projeto.status === "entregue"
              ? diasParaSuporteFim < 0
                ? `Suporte vencido há ${-diasParaSuporteFim}d`
                : `${diasParaSuporteFim}d até encerrar`
              : projeto.status === "concluido"
                ? "Encerrado"
                : undefined
          }
        />
      </div>

      {/* Avisos */}
      {feedbackAtrasado && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-3 mb-3 flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle size={14} /> O prazo do feedback do cliente passou em {fmt(projeto.prazoFeedback)}.
        </div>
      )}
      {suporteVencido && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-3 mb-3 flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle size={14} /> O suporte venceu em {fmt(projeto.fimSuporte)}. Marque como concluído.
        </div>
      )}

      {/* Botões de ação */}
      {podeOperar && (
        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
          {!projeto.dataPrimeiraEntrega && projeto.status !== "concluido" && (
            <button
              onClick={onMarcarPrimeiraEntrega}
              disabled={loading}
              className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs px-3 py-2 rounded-lg"
            >
              <Send size={12} /> Marcar primeira entrega
            </button>
          )}
          {projeto.dataPrimeiraEntrega && !projeto.feedbackRecebidoEm && projeto.status !== "concluido" && (
            <button
              onClick={onMarcarFeedbackOk}
              disabled={loading}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs px-3 py-2 rounded-lg"
            >
              <ThumbsUp size={12} /> Cliente validou — entrega final
            </button>
          )}
          {projeto.status === "entregue" && (
            <button
              onClick={onEncerrarSuporte}
              disabled={loading}
              className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white text-xs px-3 py-2 rounded-lg"
            >
              <Lock size={12} /> Encerrar suporte e concluir
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FaseCard({ icon: Icon, titulo, dataLabel, cor, subtexto }: {
  icon: LucideIcon;
  titulo: string;
  dataLabel: string;
  cor: "emerald" | "blue" | "red" | "slate";
  subtexto?: string;
}) {
  const cores: Record<string, { bg: string; text: string; border: string; iconColor: string }> = {
    emerald: { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", iconColor: "text-emerald-600" },
    blue: { bg: "bg-blue-50", text: "text-blue-800", border: "border-blue-200", iconColor: "text-blue-600" },
    red: { bg: "bg-red-50", text: "text-red-800", border: "border-red-200", iconColor: "text-red-600" },
    slate: { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", iconColor: "text-slate-400" },
  };
  const c = cores[cor];
  return (
    <div className={`rounded-lg border p-3 ${c.bg} ${c.border}`}>
      <div className={`flex items-center gap-1.5 text-xs font-medium ${c.text}`}>
        <Icon size={12} className={c.iconColor} /> {titulo}
      </div>
      <p className={`text-sm font-semibold mt-1 ${c.text}`}>{dataLabel}</p>
      {subtexto && <p className="text-[11px] text-slate-500 mt-0.5">{subtexto}</p>}
    </div>
  );
}

// A8 (Fase 02): editar clicando direto no card, sem abrir o formulário completo.
// Só gestor/admin edita (o PATCH /projetos/:id já exige isso). Data em ISO "YYYY-MM-DD"
// via slice — sem conversão de fuso pra não deslocar um dia. Ver [[date-range-tz-gotcha]].
function DataEditavel({ valorIso, podeEditar, onSalvar, extra }: {
  valorIso: string | null;
  podeEditar: boolean;
  onSalvar: (isoDia: string) => void;
  extra?: React.ReactNode;
}) {
  const [editando, setEditando] = useState(false);
  // Compara no MESMO referencial do slice (UTC/string) pra não deslocar dia por fuso.
  const iso10 = valorIso && valorIso.slice(0, 4) >= "2000" ? valorIso.slice(0, 10) : "";
  if (editando) {
    return (
      <input
        type="date"
        defaultValue={iso10}
        autoFocus
        onBlur={(e) => { const v = e.target.value; if (v && v !== iso10) onSalvar(v); setEditando(false); }}
        onKeyDown={(e) => { if (e.key === "Escape") setEditando(false); if (e.key === "Enter") e.currentTarget.blur(); }}
        className="px-2 py-1 rounded border border-blue-300 text-base font-normal outline-none w-full"
      />
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <span
        onClick={() => { if (podeEditar) setEditando(true); }}
        title={podeEditar ? "Clique para editar" : undefined}
        className={podeEditar ? "cursor-pointer rounded px-1 -mx-1 hover:bg-slate-100" : ""}
      >
        {iso10 ? iso10.split("-").reverse().join("/") : "—"}
      </span>
      {extra}
    </div>
  );
}

function ResponsavelEditavel({ atualId, atualNome, podeEditar, onSalvar }: {
  atualId: string;
  atualNome: string;
  podeEditar: boolean;
  onSalvar: (id: string) => void;
}) {
  const [editando, setEditando] = useState(false);
  // <select> CONTROLADO começando no responsável atual — evita cair no 1º item
  // enquanto a lista carrega (race) ou quando o responsável atual não é gestor/admin.
  const [sel, setSel] = useState(atualId);
  const { data: gestores = [] } = useQuery({ queryKey: ["gerentes"], queryFn: listarGerentes, enabled: podeEditar });
  const lista = gestores as Array<{ id: string; nome: string }>;
  // Garante que o responsável atual SEMPRE apareça como opção (pode ser funcionário
  // ou gestor inativado, que não vêm de /usuarios/gerentes).
  const opcoes = lista.some((g) => g.id === atualId) ? lista : [{ id: atualId, nome: atualNome }, ...lista];
  if (editando) {
    return (
      <select
        autoFocus
        value={sel}
        onChange={(e) => { const v = e.target.value; setSel(v); if (v && v !== atualId) onSalvar(v); setEditando(false); }}
        onBlur={() => setEditando(false)}
        className="px-2 py-1 rounded border border-blue-300 text-base font-normal outline-none w-full"
      >
        {opcoes.map((g) => (
          <option key={g.id} value={g.id}>{g.nome}</option>
        ))}
      </select>
    );
  }
  return (
    <span
      onClick={() => { if (podeEditar) { setSel(atualId); setEditando(true); } }}
      title={podeEditar ? "Clique para editar" : undefined}
      className={podeEditar ? "cursor-pointer rounded px-1 -mx-1 hover:bg-slate-100" : ""}
    >
      {atualNome}
    </span>
  );
}

function InfoCard({
  icon: Icon,
  label,
  value,
  danger,
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <div className={`bg-white rounded-xl border p-5 ${danger ? "border-red-200 bg-red-50" : "border-slate-200"}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon size={16} className={danger ? "text-red-500" : "text-slate-400"} />
        <span className={`text-sm ${danger ? "text-red-700" : "text-slate-500"}`}>{label}</span>
      </div>
      <div className="font-semibold text-lg text-slate-800">{value}</div>
    </div>
  );
}

